import type { Directive } from 'vue'

/**
 * `v-reveal` — settle an element into place when it scrolls into view.
 *
 * The front page has one long scroll, so a section that is simply *there* when
 * you arrive reads as a wall. This walks each marked element in as it enters
 * the viewport; the stagger comes from an inline `--d` delay or a
 * `transition-delay` style on the element itself.
 *
 * Motion is decoration here, never information: under
 * `prefers-reduced-motion: reduce` the element is revealed immediately and the
 * CSS transition has already been zeroed by style.css, so the page is fully
 * readable with no animation at all.
 */
export const vReveal: Directive<HTMLElement> = {
  mounted(el) {
    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (reduced || typeof IntersectionObserver === 'undefined') {
      el.classList.add('is-revealed')
      return
    }

    // Already on screen — above the fold, or a page short enough that this
    // element never scrolls into view. Settle it now rather than waiting on the
    // observer's first callback: `.reveal` starts at `opacity: 0`, and content
    // that is invisible until a callback arrives is content that disappears if
    // the callback does not.
    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      el.classList.add('is-revealed')
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          el.classList.add('is-revealed')
          observer.disconnect()
        }
      },
      // A little inside the fold, so the element has finished rising by the
      // time it is comfortably in view rather than animating at the edge.
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 },
    )
    observer.observe(el)
  },
  unmounted(el) {
    // The observer disconnects itself on first intersection; nothing to release
    // when an element is removed before that (the page is not re-entered).
    el.classList.remove('is-revealed')
  },
}
